// Logo upload helpers shared by the card editor. Kept framework-free and pure
// so the validation + aspect-ratio rules can be unit-tested without rendering
// the (heavy) MyCard component.

export const MAX_LOGO_BYTES = 2 * 1024 * 1024; // 2MB

export type LogoStyle = 'wide' | 'square';

/**
 * Validates a candidate logo file. Companies use any size/aspect they like, so
 * the only rules are: it must be an image, and it must respect the 2MB cap.
 * Returns null when valid, or a user-facing error message when not.
 */
export function validateLogoFile(file: File): string | null {
  if (!file.type.startsWith('image/')) return 'Please upload an image file';
  if (file.size > MAX_LOGO_BYTES) return 'Image must be under 2MB';
  return null;
}

/**
 * Picks the rendering style from the image's intrinsic dimensions so a logo
 * never silently lands in the wrong (non-rendering) slot. Images noticeably
 * wider than tall are 'wide'; everything else is 'square'.
 */
export function styleForDimensions(width: number, height: number): LogoStyle {
  return width > height * 1.4 ? 'wide' : 'square';
}

export interface LogoSlots {
  logoStyle: LogoStyle;
  wideLogo: string | null;
  squareLogo: string | null;
}

/**
 * Routes an uploaded image to the slot that matches its detected style, always
 * clearing the other slot. The returned logoStyle and the populated slot are
 * derived from the same `style` value, so they can never diverge — preventing
 * the "logo not taking" bug where the image sits in one slot while logoStyle
 * points at the empty one.
 */
export function routeLogo(dataUrl: string, style: LogoStyle): LogoSlots {
  return style === 'wide'
    ? { logoStyle: 'wide', wideLogo: dataUrl, squareLogo: null }
    : { logoStyle: 'square', squareLogo: dataUrl, wideLogo: null };
}

// A data URL is base64-encoded after the comma. Each 4 base64 chars encode 3
// bytes; trailing '=' padding shaves 1 byte each. This estimates the decoded
// byte size without allocating a Blob, so the 2MB cap can be re-checked on a
// re-encoded (rotated) data URL.
export function dataUrlByteSize(dataUrl: string): number {
  const comma = dataUrl.indexOf(',');
  const b64 = comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl;
  const padding = b64.endsWith('==') ? 2 : b64.endsWith('=') ? 1 : 0;
  return Math.floor((b64.length * 3) / 4) - padding;
}

/** True when the (re-encoded) data URL still fits under the 2MB stored cap. */
export function isUnderLogoCap(dataUrl: string): boolean {
  return dataUrlByteSize(dataUrl) <= MAX_LOGO_BYTES;
}

/**
 * Result of rotating a logo: a new PNG data URL with the rotation baked into
 * the pixels, plus the post-rotation dimensions (width/height swapped).
 */
export interface RotatedLogo {
  dataUrl: string;
  width: number;
  height: number;
}

/**
 * Rotates a logo 90° clockwise, baking the rotation into the pixels. Loads the
 * data URL into an Image, draws it onto a canvas whose dimensions are the
 * source's swapped (h×w), and exports a PNG (PNG keeps transparency for logos).
 * Returns the new data URL and swapped dimensions, which feed styleForDimensions
 * so a rotated portrait logo (72×288 → 288×72) is correctly detected as wide.
 *
 * Canvas 2D + Image is universally supported, so no feature-detection branch is
 * needed; rejects if the image fails to decode or canvas 2D is unavailable.
 */
export function rotateImage90(dataUrl: string): Promise<RotatedLogo> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onerror = () => reject(new Error('Failed to decode image'));
    img.onload = () => {
      const w = img.width;
      const h = img.height;
      const canvas = document.createElement('canvas');
      // Swap dimensions: a w×h image becomes h×w after a 90° turn.
      canvas.width = h;
      canvas.height = w;
      const ctx = canvas.getContext('2d');
      if (!ctx) { reject(new Error('Canvas 2D unavailable')); return; }
      // Translate to the new canvas centre, rotate 90° CW, then draw the source
      // centred on the rotated axes.
      ctx.translate(h / 2, w / 2);
      ctx.rotate(Math.PI / 2);
      ctx.drawImage(img, -w / 2, -h / 2);
      resolve({ dataUrl: canvas.toDataURL('image/png'), width: h, height: w });
    };
    img.src = dataUrl;
  });
}
